// src/pages/AdminActivitySearch.tsx
import React, { useMemo, useState } from "react";
import {
  AnalyticsAudience,
  AnalyticsGranularity,
  AnalyticsPlatform,
  SearchBreakdown,
  SearchPreset,
  SearchQuerySort,
} from "../api/analyticsAdmin";
import { computeDateRange } from "../helpers/analyticsPlus";
import { autoGranularityForFilters } from "../helpers/searchTrend";
import { WidgetBoundary } from "../components/AdminDashboard/WidgetBoundary";
import ActivityNavTabs from "../components/AnalyticsPlus/ActivityNavTabs";
import {
  SearchFilterBar,
  SearchFilters,
} from "../components/AnalyticsPlus/SearchFilterBar";
import SearchFunnelOverview from "../components/AnalyticsPlus/SearchFunnelOverview";
import SearchFunnelTrend from "../components/AnalyticsPlus/SearchFunnelTrend";
import SearchQueryFunnel from "../components/AnalyticsPlus/SearchQueryFunnel";
import SearchTrending from "../components/AnalyticsPlus/SearchTrending";
import StreamProgress from "../components/AnalyticsPlus/StreamProgress";
import {
  SEARCH_BREAKDOWNS,
  searchSections,
} from "../components/AnalyticsPlus/searchSections";
import { useAnalyticsStream } from "../components/AnalyticsPlus/useAnalyticsStream";
import { useDebouncedValue } from "../components/AnalyticsPlus/useDebouncedValue";

const FILTER_SETTLE_MS = 400;

const AdminActivitySearch: React.FC = () => {
  const initialRange = computeDateRange("last30", new Date());
  const [filters, setFilters] = useState<SearchFilters>({
    preset: "Last30Days",
    from: initialRange.from,
    to: initialRange.to,
    platform: "All",
    audience: "All",
  });

  // Widget controls that shape a request; the hook re-asks for only the section
  // whose query each one changes.
  const [trendBreakdown, setTrendBreakdown] = useState<SearchBreakdown>("None");
  const [trendGranularity, setTrendGranularity] =
    useState<AnalyticsGranularity | null>(null);
  const [querySort, setQuerySort] = useState<SearchQuerySort>("Volume");
  const [queryMinSearches, setQueryMinSearches] = useState(3);
  const [trendingGranularity, setTrendingGranularity] =
    useState<AnalyticsGranularity | null>(null);
  const [trendingTopN, setTrendingTopN] = useState(10);

  const settled = useDebouncedValue(filters, FILTER_SETTLE_MS);
  // The only lifted control that is typed rather than clicked. Without this, each
  // keystroke in the number input is a fresh batch; the others are discrete
  // choices where acting immediately is what the user expects.
  const settledMinSearches = useDebouncedValue(queryMinSearches, FILTER_SETTLE_MS);
  const autoGranularity = autoGranularityForFilters(settled);

  const sections = useMemo(
    () =>
      searchSections({
        filters: settled,
        trendBreakdown,
        trendGranularity: trendGranularity ?? autoGranularity,
        querySort,
        queryMinSearches: settledMinSearches,
        trendingGranularity: trendingGranularity ?? autoGranularity,
        trendingTopN,
      }),
    [
      settled,
      autoGranularity,
      trendBreakdown,
      trendGranularity,
      querySort,
      settledMinSearches,
      trendingGranularity,
      trendingTopN,
    ]
  );

  const { state, progress } = useAnalyticsStream(sections);

  const handlePresetChange = (preset: SearchPreset) =>
    setFilters((prev) => ({ ...prev, preset }));

  const handleCustomChange = (
    patch: Partial<Pick<SearchFilters, "from" | "to">>
  ) => setFilters((prev) => ({ ...prev, ...patch }));

  const handlePlatformChange = (platform: AnalyticsPlatform) =>
    setFilters((prev) => ({ ...prev, platform }));

  const handleAudienceChange = (audience: AnalyticsAudience) =>
    setFilters((prev) => ({ ...prev, audience }));

  return (
    <div>
      <h2 className="font-medium mb-1">Activity</h2>
      <p className="text-sm text-gray-500 mb-6">
        Activity metrics have been collected since 22 June 2026, so earlier
        dates show no data.
      </p>

      <ActivityNavTabs />

      <SearchFilterBar
        filters={filters}
        onPresetChange={handlePresetChange}
        onCustomChange={handleCustomChange}
        onPlatformChange={handlePlatformChange}
        onAudienceChange={handleAudienceChange}
      />

      <StreamProgress progress={progress} />

      <WidgetBoundary name="Search trend">
        <SearchFunnelTrend
          section={state.series}
          breakdown={trendBreakdown}
          onBreakdownChange={setTrendBreakdown}
          granularity={trendGranularity ?? autoGranularity}
          onGranularityChange={setTrendGranularity}
        />
      </WidgetBoundary>

      <WidgetBoundary name="Search funnel">
        <SearchFunnelOverview
          headline={state.overview}
          breakdowns={SEARCH_BREAKDOWNS.map((breakdown) => ({
            key: breakdown.key,
            label: breakdown.label,
            section: state[breakdown.id],
          }))}
        />
      </WidgetBoundary>

      <WidgetBoundary name="Top queries">
        <SearchQueryFunnel
          section={state.queryFunnel}
          sort={querySort}
          onSortChange={setQuerySort}
          minSearches={queryMinSearches}
          onMinSearchesChange={setQueryMinSearches}
        />
      </WidgetBoundary>

      <WidgetBoundary name="Trending searches">
        <SearchTrending
          section={state.trending}
          granularity={trendingGranularity ?? autoGranularity}
          onGranularityChange={setTrendingGranularity}
          topN={trendingTopN}
          onTopNChange={setTrendingTopN}
        />
      </WidgetBoundary>
    </div>
  );
};

export default AdminActivitySearch;
